import { useCallback, useState } from "react";
import { trpc } from "../../trpc";
import { runConcurrentTasks } from "../../../shared/runConcurrentTasks";
import { runWithAutoRetry } from "../../../shared/runWithAutoRetry";
import { shouldUseInlinePersona } from "../../../shared/feishuScriptAssignment";
import {
  buildScriptAssignmentCacheLookup,
  SCRIPT_ROLE_ASSIGNMENT_CONCURRENCY,
} from "../../../shared/scriptRoleAssignmentCache";
import type { RoleAssignmentItem, ScriptRolePersona } from "../../../shared/scriptRoleAssignment";
import {
  SCRIPT_ROLE_PERSONA_TERM,
  formatScriptRoleMatchFailureError,
} from "../../../shared/scriptRoleErrors";
import type { ScriptAssignmentSessionCache } from "../../../shared/scriptRoleAssignmentCache";
import {
  areAllPersonasComplete,
  persistAssignmentCache,
} from "./assignmentHelpers";
import {
  finalizeAssignmentResult,
  waitForMatchProgressViaPolling,
} from "./scriptRoleMatchPolling";
import type { AssignmentResult, SheetReadResult, SimpleActiveItem } from "./types";
import type { ParsedScriptSheetRow } from "../../../shared/feishuScriptSheet";

export interface PipelineProgressCallbacks {
  onPersonaProgress?: (input: {
    done: number;
    total: number;
    activeItems: SimpleActiveItem[];
  }) => void;
  onMatchProgress?: (input: {
    done: number;
    total: number;
    activeItems: SimpleActiveItem[];
  }) => void;
  onStepError?: (step: "persona" | "matching", error: string, retryCountdown?: number) => void;
}

export function useScriptRolePipeline() {
  const [running, setRunning] = useState(false);
  const [assignmentResult, setAssignmentResult] = useState<AssignmentResult | null>(null);

  const runPersonaAndMatch = useCallback(
    async (
      input: {
        result: SheetReadResult;
        selectedGroup: string;
        parsedRows: ParsedScriptSheetRow[];
        sourceRows: ParsedScriptSheetRow[];
        imageTextsByFileToken: Record<string, string>;
        overwriteMode: boolean;
      },
      callbacks?: PipelineProgressCallbacks
    ): Promise<AssignmentResult> => {
      const { result, selectedGroup, parsedRows, sourceRows, overwriteMode } = input;
      let imageTextsByFileToken = { ...input.imageTextsByFileToken };

      const cacheLookup = buildScriptAssignmentCacheLookup({
        spreadsheetToken: result.spreadsheetToken,
        sheetId: result.sheetId,
        group: selectedGroup,
        parsedRows: sourceRows,
        overwriteMode,
      });

      const existingCache = await trpc.feishuSheets.getRoleAssignmentCache.query(cacheLookup);

      const personaMap: Record<string, ScriptRolePersona> = {
        ...(existingCache?.personas ?? {}),
      };
      const assignments: RoleAssignmentItem[] = [...(existingCache?.assignments ?? [])];
      const assignmentIds = new Set(assignments.map((item) => item.tempRoleId));

      const writeCache = async (prepared: {
        tempRoles: ScriptAssignmentSessionCache["tempRoles"];
        memberCount: number;
      }) => {
        await persistAssignmentCache(cacheLookup, {
          spreadsheetToken: result.spreadsheetToken,
          sheetId: result.sheetId,
          group: selectedGroup,
          sourceFingerprint: cacheLookup.sourceFingerprint,
          imageTextsByFileToken,
          tempRoles: prepared.tempRoles,
          memberCount: prepared.memberCount,
          personas: personaMap,
          assignments,
          updatedAt: new Date().toISOString(),
        });
      };

      const runOnce = async (): Promise<AssignmentResult> => {
        const prepared = await trpc.feishuSheets.prepareRoleAssignment.mutate({
          spreadsheetToken: result.spreadsheetToken,
          sheetId: result.sheetId,
          sourceFingerprint: cacheLookup.sourceFingerprint,
          group: selectedGroup,
          parsedRows: sourceRows,
          imageTextsByFileToken,
          overwriteMode,
        });

        imageTextsByFileToken = prepared.imageTextsByFileToken;
        for (const [tempRoleId, persona] of Object.entries(prepared.inlinePersonas ?? {})) {
          if (!personaMap[tempRoleId]) {
            personaMap[tempRoleId] = persona;
          }
        }

        const rolesNeedingPersona = prepared.tempRoles.filter(
          (role) => !personaMap[role.tempRoleId] && !shouldUseInlinePersona(role.utteranceCount)
        );

        if (rolesNeedingPersona.length > 0) {
          const personaFailures: Array<{ tempRoleId: string; error: string }> = [];

          await runConcurrentTasks({
            items: rolesNeedingPersona,
            concurrency: SCRIPT_ROLE_ASSIGNMENT_CONCURRENCY,
            onItemStart: ({ item }) => {
              callbacks?.onPersonaProgress?.({
                done: Object.keys(personaMap).length,
                total: prepared.tempRoles.length,
                activeItems: [
                  {
                    key: item.tempRoleId,
                    label: `正在分析气氛员${item.tempRoleId}画像`,
                  },
                ],
              });
            },
            worker: async (role) => {
              try {
                const personaResult = await trpc.feishuSheets.generateRolePersona.mutate({
                  spreadsheetToken: result.spreadsheetToken,
                  sheetId: result.sheetId,
                  group: selectedGroup,
                  sourceFingerprint: cacheLookup.sourceFingerprint,
                  parsedRows: sourceRows,
                  imageTextsByFileToken,
                  tempRoleId: role.tempRoleId,
                  overwriteMode,
                });
                return { ok: true as const, role, personaResult };
              } catch (error) {
                return {
                  ok: false as const,
                  role,
                  error: error instanceof Error ? error.message : "画像分析失败",
                };
              }
            },
            onItemComplete: ({ item, result: taskResult }) => {
              if (taskResult.ok) {
                personaMap[taskResult.personaResult.persona.tempRoleId] =
                  taskResult.personaResult.persona;
              } else {
                personaFailures.push({
                  tempRoleId: item.tempRoleId,
                  error: taskResult.error,
                });
              }
              callbacks?.onPersonaProgress?.({
                done: Object.keys(personaMap).length,
                total: prepared.tempRoles.length,
                activeItems: taskResult.ok
                  ? []
                  : [
                      {
                        key: item.tempRoleId,
                        label: `气氛员${item.tempRoleId}画像失败：${taskResult.error}`,
                      },
                    ],
              });
            },
          });

          if (personaFailures.length > 0) {
            const first = personaFailures[0]!;
            throw new Error(
              `气氛员${first.tempRoleId} ${SCRIPT_ROLE_PERSONA_TERM}分析失败：${first.error}`
            );
          }
        }

        await writeCache({
          tempRoles: prepared.tempRoles,
          memberCount: prepared.memberCount,
        });

        if (!areAllPersonasComplete(prepared.tempRoles, personaMap)) {
          throw new Error(`部分角色的${SCRIPT_ROLE_PERSONA_TERM}尚未完成，请重试失败项后再匹配`);
        }

        const rolesNeedingMatch = prepared.tempRoles.filter(
          (role) => !assignmentIds.has(role.tempRoleId) && personaMap[role.tempRoleId]
        );

        if (rolesNeedingMatch.length > 0) {
          // start 接口本身幂等：新任务会入队，活跃任务返回 alreadyRunning，失败或僵死任务续跑。
          // 每轮都请求服务端判定，避免自动重试继续使用本 hook 启动时读取到的陈旧 matchJob。
          await trpc.feishuSheets.startBatchMatchRoleAssignments.mutate({
            spreadsheetToken: result.spreadsheetToken,
            sheetId: result.sheetId,
            group: selectedGroup,
            sourceFingerprint: cacheLookup.sourceFingerprint,
            parsedRows: sourceRows,
            overwriteMode,
          });

          const finalCache = await waitForMatchProgressViaPolling({
            cacheLookup,
            tempRoles: prepared.tempRoles,
            targetAssignmentCount: prepared.tempRoles.length,
            result,
            selectedGroup,
            parsedRows,
            callbacks: {
              onProgress: ({ done, total, label, activeTempRoleId }) => {
                const activeItems: SimpleActiveItem[] = [];
                if (activeTempRoleId) {
                  activeItems.push({
                    key: activeTempRoleId,
                    label: `正在匹配气氛员${activeTempRoleId}`,
                  });
                } else if (label) {
                  activeItems.push({ key: "match", label });
                }
                callbacks?.onMatchProgress?.({ done, total, activeItems });
              },
            },
          });

          const matchFailures = finalCache.matchJob?.failures ?? [];
          for (const assignment of finalCache.assignments) {
            if (!assignmentIds.has(assignment.tempRoleId)) {
              assignments.push(assignment);
              assignmentIds.add(assignment.tempRoleId);
            }
          }

          if (matchFailures.length > 0) {
            const first = matchFailures[0]!;
            throw new Error(formatScriptRoleMatchFailureError(first.tempRoleId, first.error));
          }
        }

        if (assignments.length < prepared.tempRoles.length) {
          throw new Error("仍有角色未完成匹配");
        }

        const finalized = await finalizeAssignmentResult({
          result,
          sourceFingerprint: cacheLookup.sourceFingerprint,
          selectedGroup,
          parsedRows: sourceRows,
          imageTextsByFileToken,
          personas: Object.values(personaMap),
          assignments,
          overwriteMode,
        });

        await persistAssignmentCache(cacheLookup, {
          spreadsheetToken: result.spreadsheetToken,
          sheetId: result.sheetId,
          group: selectedGroup,
          sourceFingerprint: cacheLookup.sourceFingerprint,
          imageTextsByFileToken,
          tempRoles: finalized.tempRoles,
          memberCount: prepared.memberCount,
          personas: Object.fromEntries(
            finalized.personas.map((persona) => [persona.tempRoleId, persona])
          ),
          assignments: finalized.assignments,
          updatedAt: new Date().toISOString(),
        });

        setAssignmentResult(finalized);
        return finalized;
      };

      setRunning(true);
      try {
        return await runWithAutoRetry(runOnce, {
          delayMs: 5000,
          maxAttempts: 10,
          onRetry: (_attempt, error) => {
            const msg = error instanceof Error ? error.message : "处理失败";
            const step = msg.includes("匹配") ? "matching" : "persona";
            callbacks?.onStepError?.(step, msg);
          },
          onCountdown: (secondsLeft) => {
            callbacks?.onStepError?.("matching", "处理失败，即将自动重试", secondsLeft);
          },
        });
      } finally {
        setRunning(false);
      }
    },
    []
  );

  return {
    running,
    assignmentResult,
    setAssignmentResult,
    runPersonaAndMatch,
  };
}
